import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import { getMe } from '../api/endpoints';
import { qk } from '../api/hooks';
import { getSupportSessionId, setSupportSessionId } from '../api/support';
import type { Me, Membership, Side } from '../api/types';
import { ctxOrgParam, ctxOrgs, modeOf, resolveCtx, sideOf, type Ctx, type Mode } from '../lib/context';
import { lsGet, lsSet } from '../lib/storage';
import { logout } from '../auth/userManager';

export interface Session {
  me: Me;
  mode: Mode;
  ctx: Ctx | null;
  setCtx: (c: Ctx) => void;
  /** Организации текущего контекста. */
  orgs: Membership[];
  /** org для запросов к API (undefined — все организации стороны). */
  orgParam: string | undefined;
  side: Side;
  readOnly: boolean;
  /** Сменить или завершить режим поддержки: сбрасывает кэш и перечитывает профиль. */
  switchSupport: (sessionId: string | null) => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const s = useContext(SessionContext);
  if (!s) throw new Error('useSession вне SessionProvider');
  return s;
}

const ctxKey = (userId: string) => `labconnect.ctx.${userId}`;

export function SessionProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const meQuery = useQuery({ queryKey: qk.me, queryFn: ({ signal }) => getMe(signal), staleTime: 60_000 });
  const me = meQuery.data;
  const [chosen, setChosen] = useState<Record<string, string>>({});

  // Сохранённая сессия поддержки уже завершена на сервере — забываем её.
  const staleSupport = !!me && !me.support && !!getSupportSessionId();
  useEffect(() => {
    if (staleSupport) {
      setSupportSessionId(null);
      void qc.resetQueries();
    }
  }, [staleSupport, qc]);

  // Сервер не принял сохранённую сессию поддержки (завершена или истекла) — работаем без неё.
  const supportRejected = !!meQuery.error && !!getSupportSessionId();
  useEffect(() => {
    if (supportRejected) {
      setSupportSessionId(null);
      void qc.resetQueries();
    }
  }, [supportRejected, qc]);

  const switchSupport = useCallback(
    async (sessionId: string | null) => {
      setSupportSessionId(sessionId);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
      await qc.refetchQueries({ queryKey: qk.me });
    },
    [qc],
  );

  const value = useMemo<Session | null>(() => {
    if (!me) return null;
    const stored = chosen[me.id] ?? lsGet(ctxKey(me.id));
    const ctx = resolveCtx(stored, me.memberships);
    const mode = modeOf(me, ctx);
    return {
      me,
      mode,
      ctx,
      setCtx: (c) => {
        lsSet(ctxKey(me.id), c);
        setChosen((prev) => ({ ...prev, [me.id]: c }));
      },
      orgs: ctxOrgs(me.memberships, ctx),
      orgParam: ctxOrgParam(ctx),
      side: sideOf(mode),
      readOnly: !!me.support || me.read_only,
      switchSupport,
    };
  }, [me, chosen, switchSupport]);

  useEffect(() => {
    document.body.classList.toggle('ro', !!value?.readOnly);
  }, [value?.readOnly]);

  if (meQuery.isLoading) return <div className="center-screen loading">{t('common.loading')}</div>;
  if (!value) {
    const err = meQuery.error;
    const blocked = err instanceof ApiError && err.status === 403;
    return (
      <div className="center-screen">
        <div className="empty">
          <h1 style={{ marginBottom: 8 }}>{blocked ? t('noAccess.blockedTitle') : t('errors.meFailed')}</h1>
          {err instanceof ApiError ? err.userMessage : null}
          <br />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button className="btn" onClick={() => void meQuery.refetch()}>
              {t('common.retry')}
            </button>
            <button className="btn btn-primary" onClick={() => void logout()}>
              {t('nav.logout')}
            </button>
          </div>
        </div>
      </div>
    );
  }
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
